import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import type { ElementType } from "react";
import { FaStore, FaTruck } from "react-icons/fa";
import SearchableDestinationSelect, {
  type DestinationOption,
} from "./SearchableDestinationSelect";

export type DestinationModalRow = {
  orderNo: string;
  customerName: string;
  isLocked: boolean;
  valueKey: string;
  displayLabel?: string;
  options: DestinationOption[];
  isResolving: boolean;
};

type DestinationSelectModalProps = {
  isOpen: boolean;
  onClose: () => void;
  supplierName?: string;
  rows: DestinationModalRow[];
  loadingSuppliers?: boolean;
  allReady: boolean;
  isConfirming: boolean;
  onConfirm: () => void;
  onSelect: (orderNo: string, key: string) => void;
};

function IconCircle({
  icon,
  bg = "blue.100",
  color = "blue.800",
  size = 10,
}: {
  icon: ElementType;
  bg?: string;
  color?: string;
  size?: number;
}) {
  return (
    <Box
      w={size}
      h={size}
      borderRadius="full"
      bg={bg}
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
    >
      <Icon as={icon} color={color} boxSize={size >= 10 ? 5 : 3.5} />
    </Box>
  );
}

/**
 * Multi-hop destination picker styled to match OrCA / design mock.
 */
export default function DestinationSelectModal({
  isOpen,
  onClose,
  supplierName,
  rows,
  loadingSuppliers = false,
  allReady,
  isConfirming,
  onConfirm,
  onSelect,
}: DestinationSelectModalProps) {
  const orderCount = rows.length;
  const singleOrder = orderCount === 1;
  const orderSummary = singleOrder
    ? `Order No. ${rows[0]?.orderNo ?? ""}`
    : `${orderCount} orders`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="md"
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.400" />
      <ModalContent
        maxH="85vh"
        borderRadius="2xl"
        overflow="hidden"
        boxShadow="xl"
        mx={4}
      >
        <ModalCloseButton
          top={4}
          right={4}
          color="gray.500"
          borderRadius="full"
          _hover={{ bg: "gray.100" }}
        />
        <ModalBody pt={6} pb={2} px={6}>
          <VStack spacing={5} align="stretch">
            <HStack spacing={3} align="flex-start" pr={8}>
              <IconCircle icon={FaTruck} size={12} />
              <VStack spacing={0.5} align="flex-start">
                <Text
                  fontSize="lg"
                  fontWeight="700"
                  color="blue.900"
                  lineHeight="short"
                >
                  Choose destination
                </Text>
                <Text fontSize="sm" color="gray.500" lineHeight="short">
                  {singleOrder
                    ? "Select where this order should be sent."
                    : "Choose the destination for each order."}
                </Text>
              </VStack>
            </HStack>

            {(supplierName || orderCount > 0) && (
              <HStack
                spacing={3}
                align="center"
                bg="blue.50"
                borderRadius="xl"
                px={4}
                py={3}
              >
                <IconCircle icon={FaStore} size={9} bg="white" color="blue.800" />
                <VStack spacing={0} align="flex-start" minW={0}>
                  <Text
                    fontSize="xs"
                    color="gray.500"
                    fontWeight="500"
                    textTransform="none"
                  >
                    From
                  </Text>
                  <Text
                    fontSize="md"
                    fontWeight="700"
                    color="blue.900"
                    noOfLines={1}
                  >
                    {supplierName || "—"}
                  </Text>
                  <Text fontSize="xs" color="gray.500" noOfLines={1}>
                    {orderSummary}
                  </Text>
                </VStack>
              </HStack>
            )}

            {rows.length === 0 ? (
              <Text fontSize="sm" color="gray.500">
                No orders in this selection.
              </Text>
            ) : (
              <VStack spacing={4} align="stretch">
                {rows.map((row) => (
                  <FormControl key={row.orderNo}>
                    <FormLabel mb={2}>
                      <Text fontSize="sm" fontWeight="700" color="blue.900">
                        Select destination
                        {!singleOrder ? (
                          <Text
                            as="span"
                            fontWeight="500"
                            color="gray.500"
                            ml={2}
                          >
                            · {row.orderNo}
                          </Text>
                        ) : null}
                      </Text>
                    </FormLabel>
                    <SearchableDestinationSelect
                      valueKey={row.valueKey}
                      displayLabel={row.displayLabel}
                      options={row.options}
                      isLoading={loadingSuppliers || row.isResolving}
                      isDisabled={row.isLocked}
                      placeholder="Select destination"
                      onChange={(key) => onSelect(row.orderNo, key)}
                    />
                    {row.isLocked && (
                      <FormHelperText mt={1}>
                        At customer pickup point — destination is the customer.
                      </FormHelperText>
                    )}
                  </FormControl>
                ))}
              </VStack>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter
          borderTopWidth="1px"
          borderColor="gray.100"
          px={6}
          py={4}
          gap={3}
        >
          <Button
            variant="outline"
            borderColor="gray.300"
            color="blue.900"
            fontWeight="600"
            borderRadius="lg"
            px={5}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            bg="blue.900"
            color="white"
            fontWeight="600"
            borderRadius="lg"
            px={5}
            _hover={{ bg: "blue.800" }}
            _active={{ bg: "blue.950" }}
            _disabled={{
              bg: "blue.900",
              opacity: 0.45,
              cursor: "not-allowed",
            }}
            onClick={onConfirm}
            isDisabled={!allReady || isConfirming}
            isLoading={isConfirming}
          >
            Confirm destination
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
